import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

import api from "../../services/api";

import { User as UserModel } from "../../models/entities/user";
import UserList from "../../containers/UserList";

import UserForm from "../../containers/UserForm";

import { verifyPermission } from "../../services/auth";

import PageContainer from "../../containers/PageContainer";
import Spinner from "../../components/Spinner";

import { Checkbox } from "antd";

import {
  Addicon,
  Col,
  Container,
  Content,
  Input,
  Row,
  SearchIcon,
  Select,
  Option,
  Header,
  CheckboxContainer,
} from "./styles";

function handleFilter<T>(
  data,
  pattern?: string,
  selectCategory?: string,
  profileFilter?: number
): T[] {
  let filteredData = data;

  if (selectCategory) {
    filteredData = filteredData?.filter(
      (entity) => entity["level"] === selectCategory
    );
  }

  if (profileFilter) {
    filteredData = filteredData?.filter(
      (entity) => entity["profile"] === profileFilter
    );
  }

  if (pattern) {
    pattern = pattern?.toLowerCase();
    return filteredData?.filter((entity) => {
      return (
        entity["full_name"]?.toLowerCase()?.includes(pattern) ||
        entity["email"]?.toLowerCase()?.includes(pattern) ||
        entity["id"]?.toString()?.toLowerCase()?.includes(pattern)
      );
    });
  }

  return filteredData;
}

const User: React.FC = () => {
  const location = useLocation();
  const hasProcessedUrlRef = useRef(false);
  const [users, setUsers] = useState<UserModel[]>([]);
  const [filter, setFilter] = useState<string | undefined>();
  const [user, setUser] = useState<UserModel | null>(null);
  const [shouldSearch, setShouldSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [visibleNewUser, setVisibleNewUser] = useState(false);
  const [visibleEditUser, setVisibleEditUser] = useState(false);
  const [profiles, setProfiles] = useState<UserModel[]>([]);
  const [filterSelect, setFilterSelect] = useState<string | undefined>(
    undefined
  );
  const [profileFilter, setProfileFilter] = useState<number | undefined>(
    undefined
  );
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        
        let endpoint = "/user";
        if (showDeleted) {
          endpoint = "/user/deleted";
        }
        
        const response = await api.get(endpoint);
        
        let usersData;
        if (showDeleted) {
          usersData = Array.isArray(response.data) ? response.data : [];
        } else {
          usersData = response.data?.content || [];
        }
        
        setUsers(usersData);

        const {
          data: { content: _profiles },
        } = await api.get("/user?is_profile=true");
        const sortedProfiles = _profiles.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setProfiles(sortedProfiles);
      } catch (error: any) {
        setUsers([]);
      } finally {
        setShouldSearch(false);
        setLoading(false);
      }
    };

    if (shouldSearch) {
      fetchUser();
    }
  }, [shouldSearch, showDeleted]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const userId = urlParams.get("userId");

    if (userId && users.length > 0 && !hasProcessedUrlRef.current) {
      const foundUser = users.find((u) => u.id === parseInt(userId));
      if (foundUser) {
        setUser(foundUser);
        setVisibleEditUser(true);
        setFilter(foundUser.full_name);
        hasProcessedUrlRef.current = true;
        window.history.replaceState({}, "", "/users");
      }
    }
  }, [location.search, users]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const userId = urlParams.get("userId");

    if (!userId && hasProcessedUrlRef.current) {
      hasProcessedUrlRef.current = false;
    }
  }, [location.search]);

  return (
    <PageContainer route="Usuários">
      <Container>
        <Header>
          <Row gutter={16}>
            <Col sm={8} xs={8} className="input-filter">
              <Input
                style={{ height: "2.8rem" }}
                value={filter}
                prefix={<SearchIcon />}
                placeholder="Buscar por nome, email ou ID"
                onChange={({ target: { value } }) => setFilter(value)}
              />
            </Col>

            <Col sm={4} xs={4}>
              <Select
                placeholder="Nível"
                onChange={(value) =>
                  setFilterSelect(value as string | undefined)
                }
                allowClear
                onClear={() => setFilterSelect(undefined)}
                style={{ height: "2.8rem" }}
              >
                <Option value={1}>Master</Option>
                <Option value={2}>Full</Option>
                <Option value={3}>Admin</Option>
                <Option value={4}>Proprietário</Option>
                <Option value={5}>Usuário</Option>
              </Select>
            </Col>

            <Col sm={4} xs={4}>
              <Select
                placeholder="Cargo"
                onChange={(value) =>
                  setProfileFilter(value as number | undefined)
                }
                allowClear
                onClear={() => setProfileFilter(undefined)}
                style={{ height: "2.8rem" }}
              >
                {profiles.map((profile) => (
                  <Option key={profile.id} value={profile.id}>
                    {profile.name}
                  </Option>
                ))}
              </Select>
            </Col>

      
          </Row>

          {verifyPermission("user.create_user") && (
            <button onClick={() => setVisibleNewUser(true)}>
              <Addicon />
              <span>Novo Usuário</span>
            </button>
          )}
        </Header>

        <Content>
            <>
            {verifyPermission("user.view_deleted") && (
            <CheckboxContainer>
              <Checkbox
                checked={showDeleted}
                disabled={loading}
                onChange={(e) => {
                  setShowDeleted(e.target.checked);
                  setShouldSearch(true);
                }}
              >
                Usuários deletados
              </Checkbox>
            </CheckboxContainer>
            )}
                {loading ? (
                  <Spinner />
                ) : (
            <UserList
              user={user}
              data={handleFilter<UserModel>(
                users,
                filter,
                filterSelect,
                profileFilter
              )}
              setUser={setUser}
              setShouldSearch={setShouldSearch}
              setVisibleEditUser={setVisibleEditUser}
              profiles={profiles}
              showDeleted={showDeleted}
            />
          )}
            </>
        </Content>
        <UserForm
          visible={visibleNewUser}
          setVisible={setVisibleNewUser}
          visibleEditUser={visibleEditUser}
          setVisibleEditUser={setVisibleEditUser}
          user={user}
          users={users}
          profiles={profiles}
          setUser={setUser}
          setShouldSearch={setShouldSearch}
        />
      </Container>
    </PageContainer>
  );
};

export default User;
